"use client";

import { Accordion } from "@/components/builder/accordion/accordion";
import { AccordionHeader } from "@/components/builder/accordion/header";
import { AccordionItem } from "@/components/builder/accordion/item";
import { IMAGE_FIT_OPTIONS, IMAGE_FOCAL_POINT_OPTIONS } from "@/components/builder/builder-image";
import { Button } from "@/components/builder/button";
import ContactForm from "@/components/builder/contact-form";
import { HeroCarousel, HeroCarouselItem } from "@/components/builder/hero-carousel";
import { ProductCarousel, ProductCarouselItem } from "@/components/builder/product-carousel";
import { StoryGenreList } from "@/components/builder/stories/genre-list";
import StoriesGrid from "@/components/builder/stories/list/stories-grid";
import { StoriesGroupWithChildren } from "@/components/builder/stories/list/stories-group";
import StoriesNotFound from "@/components/builder/stories/list/stories-not-found";
import StoryHighlight from "@/components/builder/stories/list/story-highlight";
import { Typography } from "@/components/builder/typography";
import { BuilderProvider } from "@/services/builder-provider";
import { Builder } from "@builder.io/react";
import "@builder.io/widgets/dist/lib/builder-widgets-async";
import MarqueeText from "./marquee-text";
import StoriesList from "./stories/list/stories-list";

BuilderProvider.initialize();

Builder.registerComponent(Typography, {
  name: "Typography",
  noWrap: true,
  inputs: [
    {
      name: "size",
      friendlyName: "Size",
      type: "enum",
      enum: ["Display", "Header", "Headline", "Subtitle", "Body", "Minimum"],
      required: true,
      defaultValue: "Body",
    },
    {
      name: "variant",
      friendlyName: "Variant",
      type: "enum",
      enum: ["Normal", "Italic", "Light"],
      required: true,
      defaultValue: "Normal",
    },
    {
      name: "overrideElement",
      friendlyName: "Override rendered element",
      helperText: [
        "<h1>: Main heading, once per page, used for the main page title",
        "<h2>: Subheading, for major sections below <h1>",
        "<h3>: Sub-subheading, for subsections within <h2>",
        "<h4>–<h6>: Lower-level headings, for deeper hierarchy, rarely needed",
        "<p>: Paragraph, for regular body text",
        "<span>: Inline text container (neutral), for targeted styling without structural value",
      ].join("\n"),
      type: "enum",
      enum: ["h1", "h2", "h3", "h4", "h5", "h6", "p", "span"],
      required: false,
    },
    {
      name: "text",
      friendlyName: "Text",
      type: "richText",
      required: true,
      localized: true,
      defaultValue: {
        "@type": "@builder.io/core:LocalizedValue",
        Default: "Change me",
      },
    },
  ],
});

Builder.registerComponent(Button, {
  name: "CustomButton",
  noWrap: true,
  defaultStyles: {
    // Builder sets this to `column` automatically which breaks the layout 😒
    flexDirection: "row",
    marginTop: "0",
  },
  inputs: [
    {
      name: "text",
      friendlyName: "Text",
      type: "longText",
      required: true,
      defaultValue: "Change me!",
    },
    {
      name: "href",
      friendlyName: "Link",
      type: "url",
    },
    {
      name: "openInNewTab",
      friendlyName: "Open link in new tab",
      type: "boolean",
      defaultValue: false,
    },
    {
      name: "shape",
      friendlyName: "Shape",
      type: "enum",
      enum: ["Rectangle", "Rounded"],
      required: true,
      defaultValue: "Rectangle",
    },
    {
      name: "background",
      friendlyName: "Background",
      type: "enum",
      enum: ["Black", "Grey"],
      required: true,
      defaultValue: "Black",
    },
    {
      name: "variant",
      friendlyName: "Variant",
      type: "enum",
      enum: ["Filled", "Outline"],
      required: true,
      defaultValue: "Filled",
    },
    {
      name: "size",
      friendlyName: "Size",
      type: "enum",
      enum: ["Normal", "Small"],
      required: true,
      defaultValue: "Normal",
    },
    {
      name: "hasArrow",
      friendlyName: "Has arrow",
      type: "boolean",
      defaultValue: false,
    },
  ],
});

Builder.registerComponent(StoriesGroupWithChildren, {
  name: "StoriesGroup",
  description:
    "A story group loading and managing stories, all other story components should be placed inside this component.",
  inputs: [
    {
      name: "storiesPerLoad",
      type: "number",
      friendlyName: "Stories per load",
      localized: false,
      required: false,
      defaultValue: 15,
      min: 1,
      max: 100,
    },
    {
      name: "startDate",
      type: "date",
      friendlyName: "Start Date",
      localized: false,
      required: false,
    },
    {
      name: "endDate",
      type: "date",
      friendlyName: "End Date",
      localized: false,
      required: false,
    },
  ],
  image: "https://cdn-icons-png.flaticon.com/512/708/708739.png",
});

Builder.registerComponent(StoryHighlight, {
  name: "StoryHighlight",
  description:
    "A single story highlight. Can be used without the stories group. " +
    "If used in a stories group, it will not be shown if a genre filter is active. " +
    "Furthermore, the highlight story should be excluded from the stories group.",
  inputs: [
    {
      name: "story",
      type: "reference",
      model: "stories",
      friendlyName: "Story",
      localized: false,
      required: true,
    },
  ],
  image: "https://cdn-icons-png.flaticon.com/512/708/708739.png",
});

Builder.registerComponent(StoryGenreList, {
  name: "Story Genres",
  description: "Shows the story genres. Can only be used on a story page.",
  inputs: [],
  image: "https://cdn-icons-png.flaticon.com/512/708/708739.png",
});

Builder.registerComponent(StoriesList, {
  name: "StoriesList",
  requiresParent: {
    component: "StoriesGroup",
    message: "A StoriesList must be inside a StoriesGroup",
  },
  inputs: [
    {
      name: "enableInfiniteScroll",
      type: "boolean",
      friendlyName: "Enable Infinite Scroll",
      localized: false,
      required: true,
      defaultValue: false,
    },
    {
      name: "initialOffset",
      type: "number",
      friendlyName: "Initial Offset",
      localized: false,
      required: false,
      defaultValue: 0,
      min: 0,
      max: 100,
    },
    {
      name: "maxTagsToShow",
      type: "number",
      friendlyName: "Max Tags to show",
      required: true,
      defaultValue: 2,
      min: 0,
      max: 10,
    },
  ],
  image: "https://cdn-icons-png.flaticon.com/512/708/708739.png",
});

Builder.registerComponent(StoriesGrid, {
  name: "StoriesGrid",
  description: "A grid of stories. The grid can be configured.",
  requiresParent: {
    component: "StoriesGroup",
    message: "A StoriesGrid must be inside a StoriesGroup",
  },
  inputs: [
    {
      name: "initialOffset",
      type: "number",
      friendlyName: "Initial Offset",
      localized: false,
      required: false,
      defaultValue: 0,
      min: 0,
      max: 100,
    },
    {
      name: "numberOfStories",
      type: "number",
      friendlyName: "Number of Stories",
      localized: false,
      required: false,
      defaultValue: -1,
      min: 0,
      max: 100,
    },
    {
      name: "gridModes",
      helperText:
        "The grid modes for the stories. Specify the index and the grid mode for each story. " +
        "Every index that was skipped will be a 1:1 grid mode. " +
        "The last index will be repeat the whole sequence.",
      type: "list",
      friendlyName: "Grid Modes",
      localized: false,
      required: false,
      subFields: [
        {
          name: "index",
          type: "number",
          friendlyName: "Index",
          localized: false,
          required: true,
          defaultValue: 0,
          min: 0,
          max: 100,
        },
        {
          name: "gridMode",
          type: "enum",
          enum: ["1:1", "2:1", "2:2"],
          friendlyName: "Grid Mode",
          localized: false,
          required: true,
          defaultValue: "1:1",
        },
      ],
    },
  ],
  image: "https://cdn-icons-png.flaticon.com/512/708/708739.png",
});

Builder.registerComponent(MarqueeText, {
  name: "MarqueeText",
  description: "A text that scrolls in a marquee.",
  inputs: [
    {
      name: "text",
      type: "string",
      friendlyName: "Text",
      localized: true,
      required: true,
      defaultValue: "Change me!",
    },
    {
      name: "speed",
      type: "number",
      friendlyName: "Speed",
      description: "The speed of the marquee in pixels per second.",
      localized: false,
      required: true,
      defaultValue: 50,
      min: 1,
      max: 1000,
    },
    {
      name: "direction",
      type: "enum",
      enum: ["left", "right"],
      friendlyName: "Direction",
      localized: false,
      required: true,
      defaultValue: "left",
    },
    {
      name: "pauseOnHover",
      type: "boolean",
      friendlyName: "Pause on Hover",
      localized: false,
      required: true,
      defaultValue: false,
    },
  ],
  image: "https://cdn-icons-png.flaticon.com/512/708/708739.png",
});

// #region Product Carousel
Builder.registerComponent(ProductCarousel, {
  name: "ProductCarousel",
  inputs: [
    {
      name: "title",
      friendlyName: "Title",
      type: "text",
      required: true,
      localized: true,
      defaultValue: {
        "@type": "@builder.io/core:LocalizedValue",
        Default: "Change me",
      },
    },
    {
      name: "buttonShape",
      friendlyName: "Button Shape",
      type: "enum",
      enum: ["Rectangle", "Rounded"],
      required: true,
      defaultValue: "Rounded",
    },
    {
      name: "openInNewTab",
      friendlyName: "Open link in new tab",
      type: "boolean",
      defaultValue: false,
    },
    {
      name: "description",
      friendlyName: "Description",
      type: "text",
      required: true,
      localized: true,
      defaultValue: {
        "@type": "@builder.io/core:LocalizedValue",
        Default: "Change me",
      },
    },
    {
      name: "showDescriptionOnMobile",
      friendlyName: "Show description on mobile",
      type: "boolean",
      required: true,
      defaultValue: false,
    },
    {
      name: "viewMoreLink",
      friendlyName: '"View More" Link',
      type: "link",
    },
    {
      name: "viewMoreText",
      friendlyName: '"View More" Text',
      type: "text",
      localized: true,
    },

    {
      name: "swipeable",
      friendlyName: "Swipeable",
      type: "boolean",
      required: true,
      defaultValue: true,
    },
    {
      name: "draggable",
      friendlyName: "Draggable",
      type: "boolean",
      required: true,
      defaultValue: true,
    },
    {
      name: "pauseOnHover",
      friendlyName: "Pause scrolling on hover",
      type: "boolean",
      required: true,
      defaultValue: true,
    },
    {
      name: "autoPlay",
      friendlyName: "Automatically play",
      type: "boolean",
      required: true,
      defaultValue: true,
    },
    {
      name: "autoPlaySpeed",
      friendlyName: "Automatic play speed",
      helperText: "The speed of sliding when automatic play is enabled. The unit is in ms.",
      type: "number",
      required: false,
      defaultValue: 3000,
    },
    {
      name: "infinite",
      friendlyName: '"Infinite" mode',
      type: "boolean",
      required: true,
      defaultValue: true,
    },
    {
      name: "slidesToSlide",
      friendlyName: "Slides to navigate at once",
      type: "number",
      required: true,
      defaultValue: 1,
    },
  ],
  image: "https://cdn-icons-png.flaticon.com/512/708/708739.png",
});

Builder.registerComponent(ProductCarouselItem, {
  name: "Product Carousel Item",
  noWrap: true,
  requiresParent: {
    component: "ProductCarousel",
    message: "This component requires to be inside a Product Carousel",
  },
  inputs: [
    {
      name: "title",
      friendlyName: "Title",
      type: "text",
      required: true,
      localized: true,
      defaultValue: {
        "@type": "@builder.io/core:LocalizedValue",
        Default: "Change me",
      },
    },
    {
      name: "description",
      friendlyName: "Description",
      type: "longText",
      required: true,
      localized: true,
      defaultValue: {
        "@type": "@builder.io/core:LocalizedValue",
        Default: "Change me",
      },
    },
    {
      name: "image",
      type: "file",
      required: true,
      defaultValue: "https://placehold.co/320x480",
    },
    {
      name: "imagePosition",
      friendlyName: "Image focal point",
      helperText: "Which part of the image stays visible when the 4:5 card crops it.",
      type: "enum",
      enum: [...IMAGE_FOCAL_POINT_OPTIONS],
      defaultValue: "Center",
    },
    {
      name: "imageFit",
      friendlyName: "Image fit",
      helperText: "Cover fills the card and crops overflow. Contain shows the whole image.",
      type: "enum",
      enum: [...IMAGE_FIT_OPTIONS],
      defaultValue: "Cover",
    },
    {
      name: "linkText",
      type: "text",
      localized: true,
      defaultValue: {
        "@type": "@builder.io/core:LocalizedValue",
        Default: "View More",
      },
    },
    {
      name: "link",
      type: "link",
    },
  ],
  image: "https://cdn-icons-png.flaticon.com/512/708/708739.png",
});
// #endregion

// #region Hero Carousel
Builder.registerComponent(HeroCarousel, {
  name: "HeroCarousel",
  friendlyName: "Hero Carousel",
  inputs: [
    {
      name: "swipeable",
      friendlyName: "Swipeable",
      type: "boolean",
      required: true,
      defaultValue: true,
    },
    {
      name: "draggable",
      friendlyName: "Draggable",
      type: "boolean",
      required: true,
      defaultValue: true,
    },
    {
      name: "pauseOnHover",
      friendlyName: "Pause scrolling on hover",
      type: "boolean",
      required: true,
      defaultValue: true,
    },
    {
      name: "autoPlay",
      friendlyName: "Automatically play",
      type: "boolean",
      required: true,
      defaultValue: true,
    },
    {
      name: "autoPlaySpeed",
      friendlyName: "Automatic play speed",
      helperText: "The speed of sliding when automatic play is enabled. The unit is in ms.",
      type: "number",
      required: false,
      defaultValue: 3000,
    },
    {
      name: "infinite",
      friendlyName: '"Infinite" mode',
      type: "boolean",
      required: true,
      defaultValue: true,
    },
    {
      name: "slidesToSlide",
      friendlyName: "Slides to navigate at once",
      type: "number",
      required: true,
      defaultValue: 1,
    },
  ],
  image: "https://cdn-icons-png.flaticon.com/512/708/708739.png",
});

Builder.registerComponent(HeroCarouselItem, {
  name: "HeroCarouselItem",
  friendlyName: "Hero Carousel Item",
  noWrap: true,
  requiresParent: {
    component: "HeroCarousel",
    message: "This component requires to be inside a Hero Carousel",
  },
  inputs: [
    {
      name: "title",
      friendlyName: "Title",
      type: "text",
      localized: true,
    },
    {
      name: "description",
      friendlyName: "Description",
      type: "richText",
      localized: true,
    },
    {
      name: "linkText",
      friendlyName: "Link Text",
      type: "text",
      localized: true,
    },
    {
      name: "link",
      friendlyName: "Link",
      type: "link",
    },
    {
      name: "image",
      friendlyName: "Image",
      type: "file",
      required: true,
      defaultValue: "https://placehold.co/320x480",
    },
    {
      name: "imagePosition",
      friendlyName: "Image focal point",
      helperText: "Which part of the image stays visible when the slide crops it.",
      type: "enum",
      enum: [...IMAGE_FOCAL_POINT_OPTIONS],
      defaultValue: "Center",
    },
    {
      name: "imageFit",
      friendlyName: "Image fit",
      helperText: "Cover fills the slide and crops overflow. Contain shows the whole image.",
      type: "enum",
      enum: [...IMAGE_FIT_OPTIONS],
      defaultValue: "Cover",
    },

    {
      name: "textPosition",
      type: "enum",
      required: true,
      enum: ["Top left", "Bottom left", "Centered"],
      defaultValue: "Top Left",
    },
  ],
  image: "https://cdn-icons-png.flaticon.com/512/708/708739.png",
});
// #endregion

Builder.registerComponent(Accordion, {
  name: "Accordion",
  description: "A container for the list of accordion items.",
  image: "https://cdn-icons-png.flaticon.com/512/708/708739.png",
  inputs: [],
});

Builder.registerComponent(AccordionItem, {
  name: "AccordionItem",
  description: "An item in an Accordion",
  image: "https://cdn-icons-png.flaticon.com/512/708/708739.png",
  noWrap: true,
  requiresParent: {
    message: "An AccordionItem needs to be inside an Accordion",
    component: "Accordion",
  },
  inputs: [
    {
      name: "title",
      type: "string",
      friendlyName: "Title",
      localized: true,
      required: true,
    },
    {
      name: "imageURL",
      type: "file",
      allowedFileTypes: ["jpeg", "png", "webp", "gif", "svg"],
      friendlyName: "Image",
      required: false,
    },
  ],
});

Builder.registerComponent(AccordionHeader, {
  name: "AccordionHeader",
  description: "Shows the accordion header",
  image: "https://cdn-icons-png.flaticon.com/512/708/708739.png",
  inputs: [
    {
      name: "title",
      type: "string",
      friendlyName: "Title",
      localized: true,
      required: true,
    },
    {
      name: "description",
      type: "string",
      friendlyName: "Description",
      localized: true,
      required: true,
    },
  ],
});

Builder.registerComponent(ContactForm, {
  name: "Contact Form",
  description: "Shows the contact form",
  image: "https://cdn-icons-png.flaticon.com/512/708/708739.png",
  inputs: [],
});
